/**
 * Period-bound aggregator for Meta Ad Audit reports.
 *
 * Used by Summary and Projections tabs to compute "Report Period" view
 * (vs the saved "All-time / Lifetime" view stored on `report.projections`).
 *
 * Period filter: an ad is "in period" if its `minDate` (its earliest active
 * date) falls within [period_start, period_end] of the report. This matches
 * the answer locked in earlier — we filter by minDate, not thresholdDate.
 *
 * IMPORTANT: this does NOT change classification logic. It only
 * re-aggregates the saved per-ad numbers over a filtered subset.
 * The Pass/Optimize/Fail rating for each ad stays exactly as the
 * backend computed it.
 */
/**
 * Filter an ads array to only those whose minDate falls inside the report
 * period. Returns the original array unchanged if scope is 'all'.
 */
export function filterAdsByScope(ads, scope, periodStart, periodEnd) {
    if (scope === 'all')
        return ads;
    if (!periodStart || !periodEnd)
        return ads;
    const ps = typeof periodStart === 'string' ? periodStart.slice(0, 10) : new Date(periodStart).toISOString().slice(0, 10);
    const pe = typeof periodEnd === 'string' ? periodEnd.slice(0, 10) : new Date(periodEnd).toISOString().slice(0, 10);
    return ads.filter((a) => {
        const md = a.minDate;
        return md && md >= ps && md <= pe;
    });
}
/**
 * Aggregate the (already-filtered) ads array into the same shape that
 * `report.projections` provides, so consumer components can use either
 * source interchangeably.
 *
 * NOTE on Pass ads: `afterSpend`/`afterRevenue` for Pass ads represents
 * post-threshold continued spend on winning ads — included for parity
 * with how the live tool reports this, but Pass is NOT counted as
 * "wasted" (the live projections.passSpend is the same idea).
 */
export function aggregateAds(ads, scope, periodStart, periodEnd) {
    const subset = filterAdsByScope(ads, scope, periodStart, periodEnd);
    const agg = {
        ads_in_scope: subset.length,
        counts: { Pass: 0, Optimize: 0, Fail: 0, Learning: 0, 'No Benchmark': 0 },
        totalSpend: 0,
        totalRevenue: 0,
        actualROAS: 0,
        passSpend: 0,
        passRevenue: 0,
        failWastedSpend: 0,
        failWastedRev: 0,
        optWastedSpend: 0,
        optWastedRev: 0,
        failOptWastedSpend: 0,
        failOptWastedRev: 0,
        avgPassROAS: 0,
    };
    let passROASsum = 0;
    let passROAScount = 0;
    for (const a of subset) {
        const r = a.result;
        if (r in agg.counts)
            agg.counts[r]++;
        // totalSpend / totalRevenue use atSpend+afterSpend, atRevenue+afterRevenue
        // (matches how report.projections.totalSpend is computed: sum of all
        // ad rows, before vs after threshold are both included).
        agg.totalSpend += (a.atSpend || 0) + (a.afterSpend || 0);
        agg.totalRevenue += (a.atRevenue || 0) + (a.afterRevenue || 0);
        if (a.result === 'Pass') {
            agg.passSpend += a.atSpend || 0;
            agg.passRevenue += a.atRevenue || 0;
            if (a.atROAS != null && a.atROAS > 0) {
                passROASsum += a.atROAS;
                passROAScount++;
            }
        }
        else if (a.result === 'Fail') {
            agg.failWastedSpend += a.wastedSpend || 0;
            agg.failWastedRev += a.wastedRevenue || 0;
        }
        else if (a.result === 'Optimize') {
            agg.optWastedSpend += a.wastedSpend || 0;
            agg.optWastedRev += a.wastedRevenue || 0;
        }
    }
    agg.failOptWastedSpend = agg.failWastedSpend + agg.optWastedSpend;
    agg.failOptWastedRev = agg.failWastedRev + agg.optWastedRev;
    agg.actualROAS = agg.totalSpend > 0 ? agg.totalRevenue / agg.totalSpend : 0;
    agg.avgPassROAS = passROAScount > 0 ? passROASsum / passROAScount : 0;
    return agg;
}
/**
 * Convenience: returns the projections-shaped object that should be used
 * for rendering — either the saved `report.projections` (all-time) or the
 * recomputed period-bound aggregate.
 */
export function getActiveProjections(report, scope) {
    if (scope === 'all') {
        return report.projections || {};
    }
    const ads = report.ads || [];
    return aggregateAds(ads, 'period', report.period_start, report.period_end);
}
